import { zodResolver } from "@hookform/resolvers/zod";
import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { IconWithBackground } from "@package/subframe/components/IconWithBackground";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { LinkButton } from "@package/subframe/components/LinkButton";
import { Select } from "@package/subframe/components/Select";
import { Switch } from "@package/subframe/components/Switch";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherArrowLeft,
  FeatherClock,
  FeatherCopy,
  FeatherKey,
  FeatherShieldQuestion,
  FeatherTrash,
  FeatherUserMinus,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type FieldErrors, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useOrganisation } from "@/contexts/OrganisationContext";
import { SetPinDialog } from "@/features/account-actions/SetPinDialog";
import {
  useAddNurseryStaffCommandControllerAddStaff,
  useUpdateMemberRoleCommandControllerUpdateMemberRole,
} from "@/generated/members/members";
import type { UserResponseDto } from "@/generated/model";
import {
  AddNurseryStaffDtoRole,
  UpdateMemberRoleDtoRole,
} from "@/generated/model";
import {
  useActivateUser,
  useDeactivateUser,
  useDeleteUser,
  useResetUserPassword,
  useUpdateUserCommandControllerUpdate,
} from "@/generated/users/users";
import { NURSERY_ROLE_DESCRIPTIONS } from "@/lib/roles";
import { staffUsername } from "@/lib/staff-username";
import { generateTemporaryPassword } from "@/lib/temporary-password";

const STAFF_EMAIL_DOMAIN =
  import.meta.env.VITE_STAFF_EMAIL_DOMAIN || "naud-staff.local";

/** Everything except username, which differs between creating and editing. */
const staffFormShape = {
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  role: z.enum(["MANAGER", "DEPUTY_MANAGER", "LEADER", "PRACTITIONER"]),
  nurseryId: z.string().min(1, "Location is required"),
  password: z.string().optional(),
  confirmPassword: z.string().optional(),
  temporaryPin: z.string().optional(),
  isActive: z.boolean(),
};

/**
 * On create the username is a real input: it seeds the staff-domain email when
 * no address is given, so it has to be something an address can be built from.
 */
const CREATE_USERNAME_RULE = z
  .string()
  .min(3, "Username must be at least 3 characters")
  .max(30, "Username must be less than 30 characters")
  .regex(
    /^[a-z0-9_]+$/,
    "Username can only contain lowercase letters, numbers, and underscores",
  );

/**
 * On edit it is none of those things. The field is rendered disabled, it is
 * derived from the address rather than typed, and the update payload does not
 * carry it — so the value is informational only.
 *
 * Validating it anyway made whole accounts uneditable. A real address such as
 * `matt+beth@example.com` is shown whole, since that is what the person logs in
 * with, and the create rule rejects it; the error then landed on a greyed-out
 * field nobody could correct, blocking every other change on the form including
 * deactivating the person.
 */
const EDIT_USERNAME_RULE = z.string();

function buildStaffFormSchema(username: z.ZodString) {
  return z
    .object({ ...staffFormShape, username })
    .refine(
      (data) => {
        if (data.password && data.password.length > 0) {
          return data.password.length >= 8;
        }
        return true;
      },
      {
        message: "Password must be at least 8 characters",
        path: ["password"],
      },
    )
    .refine(
      (data) => {
        if (data.password && data.password.length > 0) {
          return data.password === data.confirmPassword;
        }
        return true;
      },
      {
        message: "Passwords do not match",
        path: ["confirmPassword"],
      },
    );
}

const staffFormSchema = buildStaffFormSchema(CREATE_USERNAME_RULE);
const editStaffFormSchema = buildStaffFormSchema(EDIT_USERNAME_RULE);

type StaffFormData = z.infer<typeof staffFormSchema>;

interface LegacyStaffFormProps {
  mode: "create" | "edit";
  user?: UserResponseDto | null;
  isLoading?: boolean;
}

export function LegacyStaffForm({
  mode,
  user,
  isLoading,
}: LegacyStaffFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser, userRoleType, isPlatformAdmin } = useAuth();
  const { nurseries, selectedNursery } = useNursery();
  const { activeOrg } = useOrganisation();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [usernameManuallyEdited, setUsernameManuallyEdited] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const formInitializedRef = useRef(false);

  const isEditMode = mode === "edit";
  // The API refuses resetting your own password or changing your own status.
  const isEditingSelf = isEditMode && user?.id === currentUser?.id;
  const isNurseryManager = userRoleType === "NURSERY_MANAGER";

  const addStaff = useAddNurseryStaffCommandControllerAddStaff();
  const updateUser = useUpdateUserCommandControllerUpdate();
  const resetUserPassword = useResetUserPassword();
  const activateUser = useActivateUser();
  const deactivateUser = useDeactivateUser();
  const updateMemberRole =
    useUpdateMemberRoleCommandControllerUpdateMemberRole();
  const deleteUser = useDeleteUser();

  const existingUsername = user?.email ? staffUsername(user.email) : "";

  // Get first nursery access for edit mode
  const firstNurseryAccess = user?.nurseryAccess?.[0];
  // The API refuses changing your own role, and a nursery manager changing a
  // manager's.
  const isRoleLocked =
    isEditingSelf ||
    (isEditMode && isNurseryManager && firstNurseryAccess?.role === "MANAGER");

  // Show the PIN reset control only if PIN login is enabled for any nursery
  // the user belongs to — and never on the manager's own account: resetting
  // your own PIN is the self-service change flow, not a manager action.
  const userNurseryIds = new Set(
    (user?.nurseryAccess ?? []).map((a) => a.nurseryId),
  );
  const pinEnabledForUser =
    nurseries.some((n) => userNurseryIds.has(n.id) && n.pinLoginEnabled) &&
    user?.id !== currentUser?.id;

  const staffName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    existingUsername ||
    "this staff member";

  // Create-mode rules depend on the selected nursery and role: practitioners
  // and leaders on a PIN-enabled nursery sign in by PIN, so their password is
  // auto-generated and not required; the temporary PIN is only required when
  // the nursery has PIN login enabled.
  const createStaffFormSchema = useMemo(
    () =>
      staffFormSchema.superRefine((data, ctx) => {
        const nursery = nurseries.find((n) => n.id === data.nurseryId);
        const nurseryPinEnabled = nursery?.pinLoginEnabled ?? false;
        const pinOnlyRole =
          data.role === "PRACTITIONER" || data.role === "LEADER";
        const passwordAutoGenerated = nurseryPinEnabled && pinOnlyRole;

        if (
          !passwordAutoGenerated &&
          (!data.password || data.password.length < 8)
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["password"],
            message: "Password must be at least 8 characters",
          });
        }

        if (nurseryPinEnabled && !/^\d{4}$/.test(data.temporaryPin ?? "")) {
          ctx.addIssue({
            code: "custom",
            path: ["temporaryPin"],
            message: "Temporary PIN must be exactly 4 digits",
          });
        }
      }),
    [nurseries],
  );

  const schema = isEditMode ? editStaffFormSchema : createStaffFormSchema;

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<StaffFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      username: "",
      email: "",
      role: undefined,
      nurseryId: "",
      password: "",
      confirmPassword: "",
      temporaryPin: "",
      isActive: true,
    },
  });

  const watchedRole = watch("role");
  const watchedNurseryId = watch("nurseryId");
  const watchedFirstName = watch("firstName");
  const watchedLastName = watch("lastName");
  const watchedUsername = watch("username");
  const watchedEmail = watch("email");
  const watchedPassword = watch("password");
  const watchedConfirmPassword = watch("confirmPassword");
  const watchedTemporaryPin = watch("temporaryPin");
  const watchedIsActive = watch("isActive");

  const selectedNurseryPinEnabled =
    nurseries.find((n) => n.id === watchedNurseryId)?.pinLoginEnabled ?? false;
  const isPinOnlyRole =
    watchedRole === "PRACTITIONER" || watchedRole === "LEADER";
  // Practitioners/leaders on a PIN-enabled nursery sign in by PIN, so hide the
  // password fields and auto-generate a password on submit. PIN controls only
  // appear when the selected nursery has PIN login switched on.
  const hidePasswordFields =
    !isEditMode && selectedNurseryPinEnabled && isPinOnlyRole;
  const showTemporaryPin = !isEditMode && selectedNurseryPinEnabled;

  // Default to the current nursery when nurseries load (for create mode),
  // falling back to the first available nursery before a selection resolves
  useEffect(() => {
    const defaultNursery = selectedNursery ?? nurseries[0];
    if (!isEditMode && defaultNursery && !watchedNurseryId) {
      setValue("nurseryId", defaultNursery.id);
    }
  }, [nurseries, selectedNursery, watchedNurseryId, setValue, isEditMode]);

  // Set values from user when it loads (for edit mode) - only once
  useEffect(() => {
    if (isEditMode && user && !formInitializedRef.current) {
      const firstAccess = user.nurseryAccess?.[0];
      reset({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        username: existingUsername,
        email: user.email || "",
        isActive: user.isActive ?? true,
        role: (firstAccess?.role as StaffFormData["role"]) || undefined,
        nurseryId: firstAccess?.nurseryId || "",
        password: "",
        confirmPassword: "",
        temporaryPin: "",
      });
      setUsernameManuallyEdited(true);
      formInitializedRef.current = true;
    }
  }, [isEditMode, user, existingUsername, reset]);

  // Scroll to first error field on validation failure
  const onFormError = useCallback((errors: FieldErrors<StaffFormData>) => {
    const firstErrorKey = Object.keys(errors)[0] as keyof StaffFormData;
    if (firstErrorKey) {
      const element = document.getElementById(firstErrorKey);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
    }
  }, []);

  // Generate username from first and last name
  const generateUsername = (firstName: string, lastName: string) => {
    return `${firstName}_${lastName}`
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, "")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
  };

  const handleFirstNameChange = (value: string) => {
    setValue("firstName", value);
    if (!usernameManuallyEdited && !isEditMode) {
      const lastName = watchedLastName || "";
      setValue("username", generateUsername(value, lastName));
    }
  };

  const handleLastNameChange = (value: string) => {
    setValue("lastName", value);
    if (!usernameManuallyEdited && !isEditMode) {
      const firstName = watchedFirstName || "";
      setValue("username", generateUsername(firstName, value));
    }
  };

  const handleUsernameChange = (value: string) => {
    const cleanedValue = value.toLowerCase().replace(/[^a-z0-9_]/g, "");
    setValue("username", cleanedValue);
    setUsernameManuallyEdited(true);
  };

  const generateRandomPassword = () => {
    const password = generateTemporaryPassword();
    setGeneratedPassword(password);
    setValue("password", password);
    setValue("confirmPassword", password);
  };

  const copyPasswordToClipboard = () => {
    const password = watchedPassword || generatedPassword;
    if (password) {
      navigator.clipboard.writeText(password);
      toast.success("Password copied to clipboard");
    }
  };

  const handleDelete = async () => {
    if (!user) return;

    try {
      await deleteUser.mutateAsync({ id: user.id });
      toast.success("Staff member deleted successfully");
      await queryClient.invalidateQueries({
        predicate: (query) => {
          return (
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === "string" &&
            (query.queryKey[0].includes("/members") ||
              query.queryKey[0] === "/v1/users")
          );
        },
        refetchType: "all",
      });
      navigate({ to: "/settings/staff" });
    } catch (error: unknown) {
      let message = "Failed to delete staff member";
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response
      ) {
        const responseData = error.response.data as Record<string, unknown>;
        if (responseData.message && typeof responseData.message === "string") {
          message = responseData.message;
        }
      } else if (error instanceof Error) {
        message = error.message;
      }
      toast.error(message);
    }
  };

  const refreshStaffQueries = () =>
    queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        typeof query.queryKey[0] === "string" &&
        (query.queryKey[0].includes("/members") ||
          query.queryKey[0].includes("/users")),
      refetchType: "all",
    });

  const onSubmit = async (data: StaffFormData) => {
    if (isEditMode && user) {
      try {
        // The role change is the call most likely to be refused, so it goes
        // first: a refusal then leaves everything else unchanged.
        const initialRole = firstNurseryAccess?.role;
        if (data.role !== initialRole && firstNurseryAccess?.nurseryId) {
          await updateMemberRole.mutateAsync({
            nurseryId: firstNurseryAccess.nurseryId,
            userId: user.id,
            data: {
              role: data.role as UpdateMemberRoleDtoRole,
            },
          });
        }

        await updateUser.mutateAsync({
          id: user.id,
          data: {
            firstName: data.firstName,
            lastName: data.lastName,
          },
        });

        if (data.password) {
          await resetUserPassword.mutateAsync({
            id: user.id,
            data: { temporaryPassword: data.password },
          });
        }

        if (data.isActive !== user.isActive) {
          const changeStatus = data.isActive ? activateUser : deactivateUser;
          await changeStatus.mutateAsync({ id: user.id });
        }

        toast.success("Staff member updated successfully");
        // Refetch before navigating: leaving this page cancels a refetch still in flight.
        await refreshStaffQueries();
        navigate({ to: "/settings/staff" });
      } catch (error: unknown) {
        let message = "Failed to update staff member";
        if (
          error &&
          typeof error === "object" &&
          "response" in error &&
          error.response &&
          typeof error.response === "object" &&
          "data" in error.response
        ) {
          const responseData = error.response.data as Record<string, unknown>;
          if (
            responseData.message &&
            typeof responseData.message === "string"
          ) {
            message = responseData.message;
          }
        } else if (error instanceof Error) {
          message = error.message;
        }
        toast.error(message);
        // An earlier call in the save may have landed before the one that failed.
        await refreshStaffQueries();
      }
      return;
    }

    try {
      // If email is empty, generate from username
      const email = data.email || `${data.username}@${STAFF_EMAIL_DOMAIN}`;

      // PIN-login staff don't set a password (they sign in by PIN), so mint a
      // strong random one to back their identity. Everyone else uses the
      // password they entered. Only send a temporary PIN when the nursery has
      // PIN login enabled.
      const nursery = nurseries.find((n) => n.id === data.nurseryId);
      const nurseryPinEnabled = nursery?.pinLoginEnabled ?? false;
      const pinOnlyRole =
        data.role === "PRACTITIONER" || data.role === "LEADER";
      const password =
        data.password && data.password.length > 0
          ? data.password
          : nurseryPinEnabled && pinOnlyRole
            ? generateTemporaryPassword()
            : data.password || "";

      await addStaff.mutateAsync({
        nurseryId: data.nurseryId,
        data: {
          firstName: data.firstName,
          lastName: data.lastName,
          email,
          password,
          role: data.role as AddNurseryStaffDtoRole,
          isActive: data.isActive,
          temporaryPin: nurseryPinEnabled ? data.temporaryPin : undefined,
        },
      });

      toast.success("Staff member added successfully");

      await queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          typeof query.queryKey[0] === "string" &&
          (query.queryKey[0].includes("/members") ||
            query.queryKey[0].includes("/users")),
        refetchType: "all",
      });

      navigate({ to: "/settings/staff" });
    } catch (error: unknown) {
      let message = "Failed to add staff member";
      if (
        error &&
        typeof error === "object" &&
        "response" in error &&
        error.response &&
        typeof error.response === "object" &&
        "data" in error.response
      ) {
        const responseData = error.response.data as Record<string, unknown>;
        if (responseData.message && typeof responseData.message === "string") {
          message = responseData.message;
        }
      } else if (error instanceof Error) {
        message = error.message;
      }
      toast.error(message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  if (isEditMode && !user) {
    return (
      <div className="flex w-full flex-col items-start gap-8 bg-white animate-fade-in">
        <div className="flex w-full flex-col items-start gap-1 px-8 py-8">
          <div className="flex w-full items-center gap-2">
            <IconButton
              icon={<FeatherArrowLeft />}
              onClick={() => navigate({ to: "/settings/staff" })}
            />
            <span className="grow shrink-0 basis-0 whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
              Staff member not found
            </span>
          </div>
          <span className="text-body font-body text-subtext-color">
            The staff member you&apos;re looking for doesn&apos;t exist.
          </span>
        </div>
      </div>
    );
  }

  const isSaving =
    isSubmitting ||
    addStaff.isPending ||
    updateUser.isPending ||
    resetUserPassword.isPending ||
    activateUser.isPending ||
    deactivateUser.isPending ||
    updateMemberRole.isPending;

  return (
    <div className="flex w-full flex-col items-start bg-white animate-fade-in">
      <form
        onSubmit={handleSubmit(onSubmit, onFormError)}
        className="flex w-full flex-col items-start"
      >
        {/* Header */}
        <div className="flex w-full flex-col items-start gap-1 pb-8">
          <div className="flex w-full flex-wrap items-center gap-4">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <div className="flex w-full items-center gap-2">
                <IconButton
                  icon={<FeatherArrowLeft />}
                  onClick={() => navigate({ to: "/settings/staff" })}
                />
                <span className="grow shrink-0 basis-0 whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
                  {isEditMode ? "Edit staff member" : "Add staff member"}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-body font-body text-subtext-color">
                  {isEditMode
                    ? "Update staff member details and permissions"
                    : "Add a new staff member to"}
                </span>
                {!isEditMode && activeOrg && (
                  <Badge variant="neutral">{activeOrg.name}</Badge>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="neutral-primary"
                size="large"
                onClick={() => navigate({ to: "/settings/staff" })}
              >
                Cancel
              </Button>
              <Button type="submit" size="large" disabled={isSaving}>
                {isSaving ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="flex w-full flex-col items-start gap-6 pb-8">
          {/* Personal Information Section */}
          <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-9">
            <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start">
                <span className="w-full text-heading-3 font-heading-3 text-default-font">
                  Personal information
                </span>
                <span className="w-full text-body font-body text-subtext-color">
                  This is how this member of staff will appear on the system
                </span>
              </div>
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full min-w-[448px] flex-col items-start gap-6">
                <div className="flex w-full items-start gap-6">
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <TextField
                      className="h-auto w-full flex-none"
                      size="large"
                      label="First name"
                      error={!!errors.firstName}
                      helpText={errors.firstName?.message}
                    >
                      <TextField.Input
                        id="firstName"
                        placeholder="Enter first name"
                        value={watchedFirstName}
                        onChange={(e) => handleFirstNameChange(e.target.value)}
                      />
                    </TextField>
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <TextField
                      className="h-auto w-full flex-none"
                      size="large"
                      label="Last name"
                      error={!!errors.lastName}
                      helpText={errors.lastName?.message}
                    >
                      <TextField.Input
                        id="lastName"
                        placeholder="Enter last name"
                        value={watchedLastName}
                        onChange={(e) => handleLastNameChange(e.target.value)}
                      />
                    </TextField>
                  </div>
                </div>
                {/* Hidden honeypot fields to absorb browser autofill */}
                <div className="hidden" aria-hidden="true">
                  <input
                    type="text"
                    name="username"
                    autoComplete="username"
                    tabIndex={-1}
                  />
                  <input
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    tabIndex={-1}
                  />
                </div>
                <div className="flex w-full items-start gap-6">
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <TextField
                      className="h-auto w-full flex-none"
                      size="large"
                      label="Username"
                      error={!!errors.username}
                      helpText={errors.username?.message}
                      disabled={isEditMode}
                    >
                      <TextField.Input
                        id="staff_username"
                        name="staff_username_nofill"
                        placeholder="Enter username"
                        value={watchedUsername}
                        onChange={(e) => handleUsernameChange(e.target.value)}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        data-form-type="other"
                        data-1p-ignore="true"
                        data-lpignore="true"
                        data-bwignore="true"
                        disabled={isEditMode}
                      />
                    </TextField>
                  </div>
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <TextField
                      className="h-auto w-full flex-none"
                      size="large"
                      label="Email (optional)"
                      error={!!errors.email}
                      helpText={errors.email?.message}
                      disabled={isEditMode}
                    >
                      <TextField.Input
                        id="email"
                        type="email"
                        placeholder="Enter email"
                        value={watchedEmail || ""}
                        onChange={(e) => setValue("email", e.target.value)}
                        disabled={isEditMode}
                      />
                    </TextField>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Role and Permissions Section */}
          <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-9">
            <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start">
                <span className="w-full text-heading-3 font-heading-3 text-default-font">
                  Role and permissions
                </span>
                <span className="w-full text-body font-body text-subtext-color">
                  Give your staff members the right level of access
                </span>
              </div>
              <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-50 px-6 py-6">
                <div className="flex flex-col items-start gap-4">
                  <IconWithBackground
                    size="medium"
                    icon={<FeatherShieldQuestion />}
                  />
                  <span className="text-body-bold font-body-bold text-default-font">
                    Roles explained
                  </span>
                </div>
                <span className="text-body font-body text-subtext-color">
                  Roles allow you to control the level of functionality
                  available to the user.
                </span>
              </div>
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full items-center gap-6">
                <Select
                  className="h-auto grow shrink-0 basis-0 max-w-[49%]"
                  label="Role"
                  placeholder="Select a role"
                  size="large"
                  disabled={isRoleLocked}
                  value={watchedRole}
                  onValueChange={(value) => {
                    if (value) {
                      setValue("role", value as StaffFormData["role"]);
                    }
                  }}
                  error={!!errors.role}
                  helpText={errors.role?.message}
                >
                  {(!isNurseryManager || watchedRole === "MANAGER") && (
                    <Select.Item value="MANAGER">Manager</Select.Item>
                  )}
                  <Select.Item value="DEPUTY_MANAGER">
                    Deputy Manager
                  </Select.Item>
                  <Select.Item value="LEADER">Leader</Select.Item>
                  <Select.Item value="PRACTITIONER">Practitioner</Select.Item>
                </Select>
                <div className="flex h-10 flex-col items-start justify-end">
                  <span className="text-body font-body text-subtext-color">
                    {watchedRole
                      ? NURSERY_ROLE_DESCRIPTIONS[watchedRole]
                      : "Select a role to see its description."}
                  </span>
                </div>
              </div>
              <div className="flex w-full items-center gap-6">
                <Select
                  data-testid="assigned-location-select"
                  className="h-auto grow shrink-0 basis-0 max-w-[49%]"
                  label="Assigned location"
                  placeholder="Select a location"
                  size="large"
                  value={watchedNurseryId}
                  onValueChange={(value) => {
                    if (value) {
                      setValue("nurseryId", value);
                    }
                  }}
                  error={!!errors.nurseryId}
                  helpText={errors.nurseryId?.message}
                  disabled={isEditMode}
                >
                  {nurseries.map((nursery) => (
                    <Select.Item key={nursery.id} value={nursery.id}>
                      {nursery.name}
                    </Select.Item>
                  ))}
                </Select>
                <div className="flex h-10 flex-col items-start justify-end">
                  <span className="text-body font-body text-subtext-color">
                    Select which nursery this staff member can access.
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Security Section */}
          <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-9">
            <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start">
                <span className="w-full text-heading-3 font-heading-3 text-default-font">
                  Security
                </span>
                <span className="w-full text-body font-body text-subtext-color">
                  {isEditingSelf
                    ? "You can't reset your own password here."
                    : isEditMode
                      ? "Set a temporary password. They'll choose a new one when they next sign in."
                      : hidePasswordFields
                        ? "This staff member signs in with a PIN. Set a temporary PIN they'll replace on their first login."
                        : "Set the password for this staff member"}
                </span>
              </div>
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full min-w-[448px] flex-col items-start gap-6">
                {!hidePasswordFields && !isEditingSelf && (
                  <>
                    <div className="flex w-full flex-col items-start gap-1">
                      <TextField
                        className="h-auto w-full flex-none"
                        size="large"
                        label="Password"
                        helpText={
                          errors.password?.message ||
                          (isEditMode
                            ? "Leave blank to keep current password. Minimum 8 characters required."
                            : "Minimum 8 characters required.")
                        }
                        error={!!errors.password}
                      >
                        <TextField.Input
                          id="password"
                          type="password"
                          placeholder="Enter new password"
                          value={watchedPassword || ""}
                          onChange={(e) => setValue("password", e.target.value)}
                        />
                      </TextField>
                    </div>
                    <div className="flex w-full flex-col items-start gap-1">
                      <TextField
                        className="h-auto w-full flex-none"
                        size="large"
                        label="Confirm password"
                        error={!!errors.confirmPassword}
                        helpText={errors.confirmPassword?.message}
                      >
                        <TextField.Input
                          id="confirmPassword"
                          type="password"
                          placeholder="Confirm new password"
                          value={watchedConfirmPassword || ""}
                          onChange={(e) =>
                            setValue("confirmPassword", e.target.value)
                          }
                        />
                      </TextField>
                    </div>
                    {!isEditMode && (
                      <div className="flex w-full items-start justify-between">
                        <Button
                          type="button"
                          variant="neutral-primary"
                          icon={<FeatherKey />}
                          onClick={generateRandomPassword}
                        >
                          Generate password
                        </Button>
                        <Button
                          type="button"
                          variant="brand-tertiary"
                          icon={<FeatherCopy />}
                          onClick={copyPasswordToClipboard}
                          disabled={!watchedPassword && !generatedPassword}
                        >
                          Copy password
                        </Button>
                      </div>
                    )}
                  </>
                )}

                {showTemporaryPin && (
                  <div
                    className={`flex w-full flex-col items-start gap-1 ${
                      hidePasswordFields
                        ? ""
                        : "border-t border-solid border-secondary-beige-200 pt-6"
                    }`}
                  >
                    <TextField
                      className="h-auto w-full flex-none"
                      size="large"
                      label="Temporary PIN"
                      error={!!errors.temporaryPin}
                      helpText={
                        errors.temporaryPin?.message ||
                        "4-digit PIN for shared-device login. Share it with the staff member — they'll choose their own PIN on their first login."
                      }
                    >
                      <TextField.Input
                        id="temporaryPin"
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder="4-digit temporary PIN"
                        value={watchedTemporaryPin || ""}
                        onChange={(e) =>
                          setValue(
                            "temporaryPin",
                            e.target.value.replace(/\D/g, "").slice(0, 4),
                          )
                        }
                      />
                    </TextField>
                  </div>
                )}

                {isEditMode && user && (
                  <div className="flex w-full flex-col items-start gap-3 border-t border-solid border-secondary-beige-200 pt-6">
                    {pinEnabledForUser && (
                      <Button
                        type="button"
                        variant="neutral-secondary"
                        icon={<FeatherKey />}
                        onClick={() => setPinDialogOpen(true)}
                      >
                        Reset PIN
                      </Button>
                    )}
                    <LinkButton
                      variant="brand"
                      icon={<FeatherClock />}
                      onClick={() =>
                        navigate({
                          to: "/people/$userId/login-history",
                          params: { userId: user.id },
                        })
                      }
                    >
                      View login history
                    </LinkButton>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Account Status Section */}
          {!isEditingSelf && (
            <div className="flex w-full items-start gap-9 border-t border-solid border-secondary-beige-200 py-9">
              <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start gap-6">
                <div className="flex w-full flex-col items-start">
                  <span className="w-full text-heading-3 font-heading-3 text-default-font">
                    Account status
                  </span>
                  <span className="w-full text-body font-body text-subtext-color">
                    Control whether this staff member can access the system
                  </span>
                </div>
              </div>
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-secondary-beige-200 bg-default-background px-6 py-6">
                <div className="flex w-full items-center gap-4">
                  <div className="flex grow shrink-0 basis-0 flex-col items-start">
                    <span className="grow shrink-0 basis-0 text-heading-3 font-heading-3 text-default-font">
                      Active account
                    </span>
                    <span className="w-full text-body font-body text-subtext-color">
                      Staff member can access the system and perform their
                      duties
                    </span>
                  </div>
                  <Switch
                    checked={watchedIsActive}
                    onCheckedChange={(checked) => setValue("isActive", checked)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Footer Actions. Deleting a user is platform-admin only; managers
              and org admins deactivate from the status switch above instead. */}
          <div className="flex w-full items-center justify-between border-t border-solid border-secondary-beige-200 pt-6">
            {isEditMode && isPlatformAdmin ? (
              <Button
                type="button"
                variant="destructive-secondary"
                size="large"
                icon={<FeatherTrash />}
                onClick={() => setShowDeleteDialog(true)}
              >
                Delete staff member
              </Button>
            ) : (
              <div />
            )}
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="neutral-primary"
                size="large"
                onClick={() => navigate({ to: "/settings/staff" })}
              >
                Cancel
              </Button>
              <Button type="submit" size="large" disabled={isSaving}>
                {isSaving ? "Saving..." : "Save changes"}
              </Button>
            </div>
          </div>
        </div>
      </form>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
          <DialogTitle className="sr-only">Remove staff member</DialogTitle>
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
            <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
              <div className="flex flex-col items-center gap-6">
                <IconWithLogoBackground
                  variant="error"
                  size="x-large"
                  icon={<FeatherUserMinus />}
                />
                <div className="flex flex-col items-center gap-2">
                  <span className="w-full text-heading-2 font-heading-2 text-default-font text-center">
                    Remove staff member?
                  </span>
                  <span className="text-body font-body text-subtext-color text-center">
                    Are you sure you want to remove{" "}
                    <strong>
                      {user?.firstName} {user?.lastName}
                    </strong>
                    ? They will no longer have access to this nursery.
                  </span>
                </div>
              </div>
            </div>
            <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
              <Button
                className="h-14 w-80 flex-none"
                variant="neutral-secondary"
                size="extra-large"
                onClick={() => setShowDeleteDialog(false)}
                disabled={deleteUser.isPending}
              >
                Cancel
              </Button>
              <Button
                className="h-14 w-80 flex-none"
                variant="destructive-primary"
                size="extra-large"
                onClick={handleDelete}
                disabled={deleteUser.isPending}
              >
                {deleteUser.isPending ? "Deleting..." : "Yes, delete"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {isEditMode && user && (
        <SetPinDialog
          userId={user.id}
          displayName={staffName}
          open={pinDialogOpen}
          onOpenChange={setPinDialogOpen}
        />
      )}
    </div>
  );
}
